import clsx from "clsx";
// import { useRouter } from "next/router";
import { type ComponentProps, useCallback, useEffect, useState } from "react";

import { PopupContent } from "@features/ticket-card/components/popup-content";
import type { TicketType } from "@features/ticket-card/model";

import { MessageWrapper } from "@shared/ui/message-wrapper";
import { Popup } from "@shared/ui/popup";
import { parseLinkParams } from "@shared/utils";

import type { Chat, RatingType, SubquestionsType } from "../../model";
import type { ProgramQuestionsType } from "../../model/types";
import { Answer } from "./answer";
import { Feedback } from "./feedback";
import { Subquestion } from "./subquestion";

import s from "./chat-content.module.scss";

export type CardKey =
  | "tickets"
  | "equipments"
  | "cafes"
  | "accommodations"
  | "accommodations-program";

export type ChatContentProps = ComponentProps<"div"> & {
  chatData: Chat[];
  hasRating: boolean;
  individualStep: string | null;
  dialogueId: string | null;
  programQuestions: ProgramQuestionsType[];
  subquestionData: SubquestionsType[];
  onRefresh: (text: string) => void;
  setSubquestionAnswer: (step: string, answer: string | string[]) => void;
  scrollToBottom: (sectionId: string | null) => void;
  onRefreshProgram: (data: Chat["question"]) => void;
  onSetRating: (data: RatingType, dialogue_id: string) => void;
};

const HALF_MINUTE = 30000;

export const ChatContent = (props: ChatContentProps) => {
  const {
    chatData,
    individualStep,
    programQuestions,
    subquestionData,
    onRefresh,
    setSubquestionAnswer,
    scrollToBottom,
    onRefreshProgram,
    onSetRating,
    dialogueId,
    hasRating,
  } = props;

  // const route = useRouter();

  const [ticketData, setTicketData] = useState<TicketType | null>(null);
  const [isOpenPopup, setOpenPopup] = useState(false);
  const [isViewFeedback, setViewFeedback] = useState(false);

  const handleClosePopup = useCallback(() => {
    setOpenPopup(false);
  }, []);

  const handleOnCardClick = useCallback((key: CardKey, data: TicketType) => {
    if (key === "tickets") {
      setTicketData(data as TicketType);
      setOpenPopup(true);
      return;
    }

    if (key === "equipments") {
      window.open("/equipments");
      return;
    }

    if (key === "cafes") {
      window.open("/cafes-restaurants");
      return;
    }

    if (key === "accommodations") {
      window.open("/accommodations");
      return;
    }

    window.open(key);
  }, []);

  const handleBuy = useCallback(() => {
    if (!ticketData?.widgetLink) {
      console.warn("[Ticket] Не передана ссылка для покупки билета");
      return;
    }

    if (!window.WPOpenModal) {
      console.error("[Ticket] WPOpenModal не подключен");
      return;
    }

    const params = parseLinkParams(ticketData?.widgetLink);

    window.WPOpenModal(params);
  }, [ticketData]);

  const getContent = useCallback(() => {
    let activeSubquestionIndex = -1;
    for (let index = chatData.length - 1; index >= 0; index -= 1) {
      if (chatData[index]?.type === "subquestion") {
        activeSubquestionIndex = index;
        break;
      }
    }

    return chatData.map((item, index) => {
      switch (item.type) {
        case "question":
          return (
            <MessageWrapper
              key={`chat-item_${index + 1}`}
              variant="right-bottom"
              className={s.question}
            >
              {item.text}
            </MessageWrapper>
          );

        case "answer":
          return (
            <Answer
              key={`chat-item_${index + 1}`}
              item={item}
              onRefresh={onRefresh}
              onCardClick={handleOnCardClick}
              onRefreshProgram={onRefreshProgram}
            />
          );

        case "subquestion":
          return (
            <Subquestion
              key={`chat-item_${index + 1}`}
              data={item}
              programQuestions={programQuestions}
              subquestionData={subquestionData}
              individualStep={individualStep}
              isActiveSession={index === activeSubquestionIndex}
              setSubquestionAnswer={setSubquestionAnswer}
            />
          );

        default:
          return null;
      }
    });
  }, [
    chatData,
    individualStep,
    programQuestions,
    subquestionData,
    onRefresh,
    handleOnCardClick,
    setSubquestionAnswer,
  ]);

  useEffect(() => {
    setViewFeedback(false);

    if (hasRating) return;

    const answers = chatData.filter((item) => item.type === "answer");

    if (answers.length > 0 && answers.length <= 3) {
      const time = HALF_MINUTE + HALF_MINUTE * answers.length;

      const timeoutId = setTimeout(() => {
        setViewFeedback(true);
      }, time);

      return () => clearTimeout(timeoutId);
    }
  }, [chatData, hasRating]);

  const handleHideFeedback = useCallback(() => {
    setViewFeedback(false);
  }, []);

  return (
    <>
      <div className={clsx(s.root)}>
        <div className={clsx(s.wrapper)}>{getContent()}</div>
        {isViewFeedback && (
          <Feedback
            onHideFeedback={handleHideFeedback}
            scrollToBottom={scrollToBottom}
            onSetRating={onSetRating}
            dialogueId={dialogueId}
          />
        )}
      </div>
      <Popup
        id={`ticket-details-ai`}
        onClose={handleClosePopup}
        isOpen={isOpenPopup}
        variant="ticket"
      >
        <PopupContent
          onBuyClick={handleBuy}
          onClose={handleClosePopup}
          data={ticketData as TicketType}
        />
      </Popup>
    </>
  );
};

ChatContent.displayName = "ChatContent";
